import { useWorkspaceStore, type Workspace } from '../../stores/workspaceStore'
import { workspacesApi } from '../../services/api'

/**
 * The app's single writer of `workspace.smsConfig` (App IA v5 · phase 2c).
 *
 * ── Why this exists ─────────────────────────────────────────────────────────────────────
 *
 * `PUT /workspaces/{id}` replaces the WHOLE object — `services/business-entities/workspaces/
 * src/index.js` builds `SET #smsConfig = :smsConfig` with no per-field update expression. That
 * was harmless while exactly one screen owned the field. v5 splits it in two: Limits owns the
 * caps and quiet hours, Channels owns `enabled` and the Twilio credentials. Two surfaces each
 * PUTting the half they know about means whichever saves second silently reverts the other —
 * the merchant turns SMS on, then edits a cap, and SMS is off again with a "saved" message on
 * screen.
 *
 * So both surfaces send a PARTIAL here and this merges it onto the object the store currently
 * holds. Nothing else in the app may PUT `smsConfig`.
 *
 * ⚠️ **The store is read at CALL time, never captured.** A merge base captured at mount is
 * stale the moment another surface writes, so the merge would faithfully preserve values that
 * are no longer there.
 *
 * ⚠️ **But reading the store at call time is not the same as reading the SERVER.** The store
 * itself can be a stale snapshot, and an earlier version of this docblock claimed that class of
 * bug was closed when only half of it was:
 *
 *   · `setCurrentWorkspace` sets `currentWorkspace` and nothing else — it never reconciles
 *     `workspaces[]`, the login-time membership list.
 *   · `switchWorkspace` re-seeds `currentWorkspace` FROM `workspaces[]`, so switching away and
 *     back rewinds `smsConfig` to whatever it was at login.
 *   · `partialize` persists both to localStorage, so a second tab rehydrates the same rewind.
 *
 * Enable SMS on Channels → switch workspace → switch back → save a cap, and the merge base is
 * the pre-change snapshot: the PUT carries `enabled: false` under a "saved" message.
 *
 * What closes the loop is the response. `PUT /workspaces/{id}` runs with
 * `ReturnValues: 'ALL_NEW'` (`services/business-entities/workspaces/src/index.js`) and answers
 * `{ success, data: <the whole stored row> }`, so the mirror below takes `data.smsConfig` — what
 * was ACTUALLY stored — in preference to the locally computed `merged`. Mirroring `merged`
 * republished the stale value as fact and the divergence never healed.
 *
 * ⚠️ That NARROWS the window; it does not close it. The store converges only after a save, so
 * the first save following a switch-back still merges onto the rewound snapshot. Closing it
 * properly means reconciling `workspaces[]` (or re-reading the workspace on switch) in
 * `workspaceStore`, which is a change to that store and not to this module — MAR-155. That
 * issue covers every nested workspace attribute with this shape (`discountGovernance`,
 * `storeContext`, `emailStyles`, `agentSettings`), not just this one; do not
 * re-solve it per surface here.
 *
 * ⚠️ **What this does NOT fix, and must not be read as fixing.** Three things decide what
 * lands in the row, and this module owns only two of them:
 *
 *   1. the merge BASE — the store, read here at call time. Owned here, and only as fresh as the
 *      store is (above).
 *   2. the TARGET — the workspace, captured before the PUT and re-checked after it. Owned here.
 *   3. the PARTIAL — whatever the calling surface hands in. **Owned by the caller.**
 *
 * (3) is not a detail. `SmsConfigSection` used to seed its form in an effect guarded on
 * `currentWorkspace?.smsConfig`, so switching to a workspace that had never had SMS configured
 * left the PREVIOUS workspace's `enabled` and campaign types sitting in the form — and a save
 * then merged those onto the new workspace's defaults and turned SMS on for it. Every field in
 * that partial was correctly merged onto the correct base and written to the correct
 * workspace. It was still wrong, because the partial was someone else's.
 *
 * Both callers now scope their form state at the READ (`AssistantNameCard`'s `Draft` shape) and
 * re-check the workspace before calling in. A new caller must do the same; this module cannot
 * do it for them.
 */

export type SmsConfig = NonNullable<Workspace['smsConfig']>

/** Whatever `workspacesApi.update` resolves to — derived so it cannot drift from the client. */
export type SmsConfigWriteResult = Awaited<ReturnType<typeof workspacesApi.update>>

/**
 * A complete object to merge onto, because the PUT is a replace.
 *
 * A workspace that has never had SMS configured has no `smsConfig` at all, and merging a
 * partial onto `undefined` would write a half-shaped object the readers then fill in with
 * their own scattered fallbacks. These are the same values `SmsConfigSection` has always
 * seeded its form with, so an untouched workspace is written exactly as it was before.
 */
export const SMS_CONFIG_DEFAULTS: SmsConfig = {
  enabled: false,
  dailyLimitMessages: 100,
  monthlyLimitMessages: 1000,
  maxSmsPerCustomerMonth: 6,
  quietHoursStart: 21,
  quietHoursEnd: 8,
  enabledCampaignTypes: ['cart_abandonment']
}

/**
 * The whole `smsConfig` object a PUT of `partial` writes over `stored`. `patchSmsConfig` sends
 * exactly this, and the SMS cards' dirty check compares it against the stored object, so what
 * the SaveBar calls unsaved is what a save would actually change (D-27).
 */
export const mergeSmsConfig = (stored: SmsConfig | undefined, partial: Partial<SmsConfig>): SmsConfig => ({
  ...SMS_CONFIG_DEFAULTS,
  ...stored,
  ...partial
})

/** `workspacesApi.update` is typed against `CreateWorkspaceRequest`, which has no `smsConfig`. */
type WorkspaceUpdate = Parameters<typeof workspacesApi.update>[1]

/**
 * Merge `partial` into the current workspace's `smsConfig` and PUT the merged whole object.
 *
 * Returns the API response verbatim so the caller renders its own outcome — a refusal is the
 * caller's to show, not this module's to swallow. A thrown network error propagates for the
 * same reason.
 */
export async function patchSmsConfig(partial: Partial<SmsConfig>): Promise<SmsConfigWriteResult> {
  const asked = useWorkspaceStore.getState().currentWorkspace
  if (!asked) {
    return { success: false, error: 'No workspace is selected, so there is nothing to save to.' }
  }

  const merged = mergeSmsConfig(asked.smsConfig, partial)

  const response = await workspacesApi.update(asked.id, { smsConfig: merged } as WorkspaceUpdate)

  // ⚠️ Mirror only AFTER the server agreed, and only ever onto the workspace that was asked.
  //
  // Writing first is how the Authority ladder came to show a rung the backend had refused
  // (see the docblock in `AuthorityPanel.tsx`); writing to whatever is current after the await
  // is how a save resolving across a workspace switch stamps one store's SMS caps onto
  // another. Re-read the store rather than spreading `asked`, so a field some other save
  // changed while this one was in flight survives.
  if (!response.success) return response
  const { currentWorkspace: live, setCurrentWorkspace } = useWorkspaceStore.getState()
  if (!live || live.id !== asked.id) return response

  // ⚠️ Mirror what the SERVER stored, not what we computed. The PUT runs `ReturnValues:
  // 'ALL_NEW'` and returns the whole row as `data`, so `data.smsConfig` is the stored truth —
  // and mirroring it is what lets a store that had drifted (see the docblock) converge instead
  // of republishing its own stale merge as fact.
  //
  // `ApiResponse.data` is `any` and nothing in the client can prove the field is there, so a
  // response without it falls back to `merged`: that is the old behaviour, and it is better
  // than blanking `smsConfig` on a shape we did not expect.
  const returned = (response.data as { smsConfig?: unknown } | undefined)?.smsConfig
  const stored = returned && typeof returned === 'object' ? (returned as SmsConfig) : merged
  setCurrentWorkspace({ ...live, smsConfig: stored })

  return response
}
